import React from 'react';
import { CheckCircle, TrendingUp, Target, Database, Award } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const DataQualityPanel = ({ metrics = {} }) => {
  const qualityData = [
    { name: 'Accurate', value: metrics?.sentimentAccuracy || 0, color: '#10B981' },
    { name: 'Inaccurate', value: 100 - (metrics?.sentimentAccuracy || 0), color: '#EF4444' }
  ];

  const classificationData = [
    { name: 'Success', value: metrics?.classificationSuccess || 0, color: '#3B82F6' },
    { name: 'Failed', value: 100 - (metrics?.classificationSuccess || 0), color: '#F59E0B' }
  ];

  const getQualityColor = (value) => {
    if (value >= 90) return 'text-green-600';
    if (value >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityBg = (value) => {
    if (value >= 90) return 'bg-green-100';
    if (value >= 75) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Data Quality Metrics</h2>
          <p className="text-sm text-gray-600 mt-1">
            AI accuracy, deduplication, and content classification performance
          </p>
        </div>
        <Award className="w-6 h-6 text-blue-600" />
      </div>
      {/* Quality Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className={`p-4 rounded-lg border ${getQualityBg(metrics?.sentimentAccuracy)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Target className={`w-5 h-5 ${getQualityColor(metrics?.sentimentAccuracy)}`} />
            <span className="text-sm font-medium text-gray-700">Sentiment Accuracy</span>
          </div>
          <p className={`text-3xl font-bold ${getQualityColor(metrics?.sentimentAccuracy)}`}>
            {metrics?.sentimentAccuracy?.toFixed(1)}%
          </p>
        </div>

        <div className="p-4 rounded-lg border bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Duplicate Rate</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {metrics?.duplicateRate?.toFixed(1)}%
          </p>
        </div>

        <div className={`p-4 rounded-lg border ${getQualityBg(metrics?.classificationSuccess)}`}>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className={`w-5 h-5 ${getQualityColor(metrics?.classificationSuccess)}`} />
            <span className="text-sm font-medium text-gray-700">Classification</span>
          </div>
          <p className={`text-3xl font-bold ${getQualityColor(metrics?.classificationSuccess)}`}>
            {metrics?.classificationSuccess?.toFixed(1)}%
          </p>
        </div>

        <div className={`p-4 rounded-lg border ${getQualityBg(metrics?.dataCompleteness)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Database className={`w-5 h-5 ${getQualityColor(metrics?.dataCompleteness)}`} />
            <span className="text-sm font-medium text-gray-700">Completeness</span>
          </div>
          <p className={`text-3xl font-bold ${getQualityColor(metrics?.dataCompleteness)}`}>
            {metrics?.dataCompleteness?.toFixed(1)}%
          </p>
        </div>

        <div className={`p-4 rounded-lg border ${getQualityBg(metrics?.averageConfidence)}`}>
          <div className="flex items-center gap-2 mb-2">
            <Award className={`w-5 h-5 ${getQualityColor(metrics?.averageConfidence)}`} />
            <span className="text-sm font-medium text-gray-700">Confidence</span>
          </div>
          <p className={`text-3xl font-bold ${getQualityColor(metrics?.averageConfidence)}`}>
            {metrics?.averageConfidence?.toFixed(1)}%
          </p>
        </div>
      </div>
      {/* Quality Visualization Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sentiment Accuracy Chart */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Sentiment Analysis Accuracy</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={qualityData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {qualityData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Classification Success Chart */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Content Classification</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={classificationData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {classificationData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Quality Trends */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">Quality Trends</h4>
            <p className="text-sm text-blue-800">
              Sentiment accuracy improved by 2.3% in the last 24 hours. Classification success rate 
              maintained above 95% target. Duplicate detection rate remains optimal at {metrics?.duplicateRate?.toFixed(1)}%.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityPanel;